import { Component } from "react"
import PropTypes from "prop-types"

import GrowingTextArea from "shared/growing_text_area"

import StepField from "./step_field"

import { capitalize } from "humanize-plus"

import iconAction from "images/icons/icon-new-connection-action.svg"
import iconQuery from "images/icons/icon-new-connection-query.svg"
import iconTrigger from "images/icons/icon-new-connection-trigger.svg"

import "./dynamic_applet.scss"

class Step extends Component {
  constructor(props) {
    super(props)

    const stepFieldValues = {}
    const onUpdate = {}
    for (let field of this.props.fields) {
      const stepField = field[`${this.props.step_type}_field`]
      const value_key = this.props.full_module_name + "." + stepField.name
      stepFieldValues[stepField.name] = field.hidden
        ? JSON.parse(field.default_value_json)
        : this.props.live_values[value_key] || ""

      if (stepField.field_ui_type.indexOf("location") > -1) {
        onUpdate[stepField.name] = this.onLocationFieldUpdated(stepField.name)
      } else {
        onUpdate[stepField.name] = this.handleStepFieldUpdate
      }
    }

    this.state = {
      stepFieldValues,
      onUpdate,
      enableThisClicked: false,
    }
  }

  onLocationFieldUpdated = fieldName => {
    return locationState => {
      this.setState({
        stepFieldValues: {
          ...this.state.stepFieldValues,
          [fieldName]: locationState,
        },
      })
    }
  }

  curlCommand = () => {
    return `curl -i -X "POST" "https://connect.ifttt.com/v2/connections/${this.props.connection_id}${this.props.step_path}?user_id=${this.props.channel_user_id}" \\
  -H 'IFTTT-Service-Key: API_KEY' \\
  -H 'Content-Type: application/json; charset=utf-8'`
  }

  escapeRegExp = text => {
    return text.toString().replace(/[-[\]{}()*+?.,\\^$|#\s]/g, "\\$&")
  }

  // Add a background color for variables in GrowingTextArea cURL examples
  transformMirrorContentCurl = html => {
    let transformedContent = html.replace(
      /API_KEY|USER_ID|REQUIRED|OPTIONAL/g,
      match => `<span style='background-color: #eee;'>${match}</span>`
    )
    return transformedContent
  }

  handleStepFieldUpdate = event => {
    this.setState({
      stepFieldValues: {
        ...this.state.stepFieldValues,
        [event.target.name]: event.target.value,
      },
    })
  }

  handleStepFieldClear = event => {
    this.setState({
      stepFieldValues: {
        ...this.state.stepFieldValues,
        [event.target.name]: "",
      },
    })
  }

  handleEnableThisClick = () => {
    if (!this.props.enabled_for_user) {
      setTimeout(() => {
        window.location.hash = this.props.anchor
        this.setState({ enableThisClicked: true })
      }, 100)
    }
  }

  icon() {
    switch (this.props.step_type) {
      case "action":
        return iconAction
      case "trigger":
        return iconTrigger
      case "query":
        return iconQuery
    }
  }

  render() {
    return (
      <section styleName="dynamic-applet-step" id={this.props.anchor}>
        <header
          style={{
            backgroundColor: "#" + this.props.channel_brand_color,
          }}
        >
          <h3>
            <span
              styleName="svg-icon"
              title={this.props.step_type}
              alt={this.props.step_type}
              style={{
                color: "#" + this.props.channel_brand_color,
              }}
              dangerouslySetInnerHTML={{ __html: this.icon() }}
            />
            <span styleName="name">{this.props.name}</span>
          </h3>
          <p styleName="description">{this.props.description}</p>
        </header>

        <article>
          <h3>Test this {this.props.step_type}</h3>

          <p>
            {this.props.enabled_for_user ? "Configure" : "Enable"} this
            connection to generate a cURL command that you can paste into your
            Terminal to test this {this.props.step_type}.
          </p>

          <ul>
            <li>
              <ul>
                <li>
                  {this.state.enableThisClicked ? (
                    <a
                      href="javascript:document.location.reload();"
                      className="button-tertiary button-outlined"
                    >
                      Click here to reload this page
                    </a>
                  ) : (
                    <a
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={this.handleEnableThisClick}
                      href={
                        this.props.enabled_for_user
                          ? `${this.props.connect_url}/edit`
                          : this.props.connect_url
                      }
                      className="button-tertiary"
                    >
                      {this.props.enabled_for_user ? "Configure" : "Enable"}{" "}
                      connection
                    </a>
                  )}
                </li>
              </ul>
            </li>

            <li>
              Use this cURL command to test
              <ul>
                <li
                  styleName="growing-text-area-container no-margin-top"
                  title="Click to copy example"
                  alt="Click to copy example"
                >
                  <GrowingTextArea
                    type="text"
                    value={this.curlCommand()}
                    showMirror={true}
                    transformMirrorContent={this.transformMirrorContentCurl}
                    data-copy-field-on-click="cURL command"
                    readOnly
                  />
                </li>
                <li>
                  Your <strong>IFTTT-Service-Key</strong> can be found in the{" "}
                  <a
                    href={this.props.mktApiUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="link-inline"
                  >
                    API tab
                  </a>{" "}
                  of the IFTTT Platform under the Service Key heading. Remember,
                  running this command affects your own connected account.
                </li>
              </ul>
            </li>

            <li>
              {capitalize(this.props.step_type)} field values
              <ul>
                {this.props.enabled_for_user ? (
                  <>
                    {this.props.fields.map(
                      s => s[`${this.props.step_type}_field`]
                    ).length == 0 && (
                      <li>
                        No fields to configure for this {this.props.step_type}.
                      </li>
                    )}
                    {this.props.fields.map(
                      s => s[`${this.props.step_type}_field`]
                    ).length > 0 && (
                      <li>
                        The cURL command will{" "}
                        {this.props.step_type === "action" ? "run" : "perform"}{" "}
                        this {this.props.step_type} with the values below. To
                        update these values, enable and configure the
                        connection.
                      </li>
                    )}
                    {this.props.fields
                      .map(s => s[`${this.props.step_type}_field`])
                      .map((stepField, index) => (
                        <li key={index}>
                          <StepField
                            id={stepField.id}
                            name={stepField.name}
                            label={stepField.label}
                            helper_text={stepField.helper_text}
                            field_type_definition_slug={
                              stepField.field_type_definition_slug
                            }
                            required={stepField.required}
                            value={this.state.stepFieldValues[stepField.name]}
                            onUpdate={this.state.onUpdate[stepField.name]}
                            onClear={this.handleStepFieldClear}
                            docs_url={this.props.docs_url}
                            step_type={this.props.step_type}
                          />
                        </li>
                      ))}
                  </>
                ) : (
                  <li>
                    Enable this connection to see {this.props.step_type} field
                    values.
                  </li>
                )}
              </ul>
            </li>
          </ul>
        </article>
      </section>
    )
  }
}

Step.propTypes = {
  step_type: PropTypes.string.isRequired,
  step_type_sentence: PropTypes.string.isRequired,
  step_path: PropTypes.string.isRequired,
  step_field_options_path: PropTypes.string.isRequired,
  enabled_for_user: PropTypes.bool.isRequired,
  connection_id: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  channel_brand_color: PropTypes.string.isRequired,
  channel_name: PropTypes.string.isRequired,
  full_module_name: PropTypes.string.isRequired,
  fields: PropTypes.array.isRequired,
  channel_user_id: PropTypes.string.isRequired,
  connect_url: PropTypes.string.isRequired,
  channel_image_url: PropTypes.string.isRequired,
  docs_url: PropTypes.string.isRequired,
  mktApiUrl: PropTypes.string.isRequired,
  anchor: PropTypes.string.isRequired,
  live_values: PropTypes.object.isRequired,
}

export default Step
